/**
 * @fileoverview Source-citation helpers for generated reports (MOR-556).
 *
 * Pure (non-component) utilities for the inline `data-morgan-cite` citations:
 * encode/decode, parse the distinct cited sources from a report's HTML, strip
 * them, and build a plain bibliography for export.
 *
 * Two span formats coexist (MOR-919):
 * - New: `<span data-morgan-cite='[<sources>]' data-cite-text="1">referenced
 *   text</span>` — the attr is a JSON ARRAY and the span wraps the report text
 *   the source supports (rendered as a subtle underline). Stripping UNWRAPS
 *   these (the text is report content).
 * - Legacy: `<span data-morgan-cite='<source>'>Source title</span>` — the attr
 *   is a single JSON object and the span's text is the source title (the old
 *   chip). Stripping REMOVES these entirely.
 */

import { getKindMeta } from "@/components/agents/universal/searchResultKinds";
import type { CitedSource } from "@/components/agents/universal/sourceCitations.helpers";

/** Decode a `data-morgan-cite` attr into its source list (1+ entries). */
export function citationsFromAttr(attr: string): CitedSource[] {
  try {
    const json = attr.replace(/&#39;/g, "'").replace(/&lt;/g, "<");
    const parsed = JSON.parse(json) as CitedSource | CitedSource[];
    const arr = Array.isArray(parsed) ? parsed : [parsed];
    return arr.filter(
      (o) => o && typeof o.kind === "string" && typeof o.id === "string",
    );
  } catch {
    return [];
  }
}

export function encodeCitationsAttr(sources: CitedSource[]): string {
  return JSON.stringify(sources).replace(/'/g, "&#39;").replace(/</g, "&lt;");
}

/** Parse the distinct cited sources out of a report's HTML. */
export function parseCitedSources(html: string): CitedSource[] {
  const re = /<span\s+data-morgan-cite='([\s\S]*?)'/gi;
  const seen = new Set<string>();
  const out: CitedSource[] = [];
  for (const m of html.matchAll(re)) {
    for (const source of citationsFromAttr(m[1])) {
      const key = `${source.kind}:${source.id}`;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(source);
    }
  }
  return out;
}

/**
 * Strip inline citations from report HTML: text-wrapping citation spans are
 * unwrapped (their content is report text), legacy title-chip spans are
 * removed. DOM-based so nested markup inside a wrapped span survives.
 */
export function stripCitations(html: string): string {
  const doc = new DOMParser().parseFromString(
    `<div id="morgan-strip-root">${html}</div>`,
    "text/html",
  );
  const root = doc.getElementById("morgan-strip-root");
  if (!root) return html;
  root.querySelectorAll("span[data-morgan-cite]").forEach((el) => {
    if (el.hasAttribute("data-cite-text")) {
      el.replaceWith(...Array.from(el.childNodes));
    } else {
      el.remove();
    }
  });
  return root.innerHTML;
}

/** Stable key for a cited source. */
export function sourceKey(source: CitedSource): string {
  return `${source.kind}:${source.id}`;
}

/** Wraps a legacy citation's source-title text; hidden in `index.css`. */
export const CITE_TITLE_CLASS = "report-cite-title";

/**
 * The superscript marker for a citation: an empty span carrying the number in
 * `data-cite-n` (drawn by CSS) and the sources it stands for, so the citation
 * layer can reveal their chips on hover. `data-cite-marker` marks it as the
 * marker itself, which is the only hoverable part on marker-only surfaces.
 */
export function citationMarkerElement(
  marker: string,
  sources: CitedSource[],
  doc: Document = document,
): HTMLElement {
  const el = doc.createElement("span");
  el.setAttribute("data-morgan-cite", encodeCitationsAttr(sources));
  el.setAttribute("data-cite-n", marker);
  el.setAttribute("data-cite-marker", "");
  return el;
}

/**
 * Bibliography numbering: each distinct source gets the number of its first
 * appearance in the report, so an inline marker and the "Sources" list at the
 * end always agree (MOR-924).
 */
export function buildCitationNumbers(
  sources: CitedSource[],
): Map<string, number> {
  return new Map(sources.map((s, i) => [sourceKey(s), i + 1]));
}

/** The marker for a citation span: "3", or "3,4" when it cites several. */
export function citationMarker(
  sources: CitedSource[],
  numbers: Map<string, number>,
): string {
  return sources
    .map((s) => numbers.get(sourceKey(s)))
    .filter((n): n is number => typeof n === "number")
    .join(",");
}

/**
 * The last sentence in `text`, as a `[start, end)` offset pair with trailing
 * whitespace excluded. Mirrors the wrap boundary the writer pipeline uses
 * server-side (`convex/jobs/reporting/citations.ts`): the sentence runs back
 * to the nearest punctuation-then-whitespace break.
 */
function lastSentenceRange(
  text: string,
): { start: number; end: number } | null {
  const end = text.replace(/\s+$/, "").length;
  if (end === 0) return null;
  let start = 0;
  for (let i = end - 1; i >= 1; i--) {
    if (/\s/.test(text[i]) && /[.!?;:]/.test(text[i - 1])) {
      start = i + 1;
      break;
    }
  }
  while (start < end && /\s/.test(text[start])) start += 1;
  return start < end ? { start, end } : null;
}

/**
 * Text nodes in `block` that precede `stop` and aren't already claimed by an
 * earlier citation — so back-to-back citations don't fight over one sentence,
 * and the second of a pair simply has nothing left to wrap.
 */
function unclaimedTextBefore(block: Element, stop: Element): Text[] {
  const walker = block.ownerDocument.createTreeWalker(
    block,
    NodeFilter.SHOW_TEXT,
  );
  const out: Text[] = [];
  for (let n = walker.nextNode(); n; n = walker.nextNode()) {
    if (!(stop.compareDocumentPosition(n) & Node.DOCUMENT_POSITION_PRECEDING)) {
      break;
    }
    if (n.parentElement?.closest("span[data-morgan-cite]")) {
      out.length = 0;
      continue;
    }
    out.push(n as Text);
  }
  return out;
}

/**
 * Drop the last `count` characters of text preceding `el` in its parent, so a
 * known-length run can be removed without depending on how the browser split
 * text nodes.
 */
function dropTextBefore(el: Element, count: number): void {
  let remaining = count;
  let node = el.previousSibling;
  while (node && remaining > 0 && node.nodeType === Node.TEXT_NODE) {
    const before = node.previousSibling;
    const text = node as Text;
    const cut = Math.min(remaining, text.data.length);
    text.data = text.data.slice(0, text.data.length - cut);
    remaining -= cut;
    if (!text.data) text.remove();
    node = before;
  }
}

/** Resolve a flat offset across `nodes` to a DOM position. */
function domPosition(
  nodes: Text[],
  offset: number,
): { node: Text; offset: number } {
  const resolved = nodes.reduce<{
    remaining: number;
    found: { node: Text; offset: number } | null;
  }>(
    (acc, node) => {
      if (acc.found) return acc;
      if (acc.remaining <= node.data.length) {
        return { ...acc, found: { node, offset: acc.remaining } };
      }
      return { remaining: acc.remaining - node.data.length, found: null };
    },
    { remaining: offset, found: null },
  ).found;
  if (resolved) return resolved;
  const last = nodes[nodes.length - 1];
  return { node: last, offset: last.data.length };
}

/**
 * Move the sentence a legacy citation follows into the citation span, turning
 * it into a text-wrapping citation. Returns false when there's no sentence to
 * claim (the citation opens its block, or a sibling citation took it).
 */
function wrapPrecedingSentence(el: Element): boolean {
  const doc = el.ownerDocument;
  const block = el.parentElement;
  if (!doc || !block) return false;
  const nodes = unclaimedTextBefore(block, el);
  if (nodes.length === 0) return false;
  const text = nodes.map((n) => n.data).join("");
  const sentence = lastSentenceRange(text);
  if (!sentence) return false;

  const from = domPosition(nodes, sentence.start);
  const to = domPosition(nodes, sentence.end);
  const range = doc.createRange();
  range.setStart(from.node, from.offset);
  range.setEnd(to.node, to.offset);
  el.textContent = "";
  el.appendChild(range.extractContents());

  // The whitespace that separated the sentence from the citation now sits
  // between them; move it after so the marker hugs the text it annotates.
  const gap = text.length - sentence.end;
  if (gap > 0) {
    dropTextBefore(el, gap);
    el.after(doc.createTextNode(" "));
  }
  return true;
}

/**
 * Stamp each inline citation in a rendered report with its bibliography
 * number (`data-cite-n`, drawn as a superscript by CSS).
 *
 * Legacy citations carry the source's title as their own text — which reads as
 * if the title were part of the sentence. They're upgraded to the current
 * format instead: the title is dropped and the sentence the citation follows
 * is pulled inside the span, so the passage the source supports gets the same
 * underline (and hover/click target) as a natively wrapped citation. Where
 * there's no sentence to claim, only the number remains.
 */
export function numberCitationsInDom(
  root: ParentNode,
  numbers: Map<string, number>,
): void {
  root.querySelectorAll("span[data-morgan-cite]").forEach((el) => {
    const sources = citationsFromAttr(
      el.getAttribute("data-morgan-cite") ?? "",
    );
    const marker = citationMarker(sources, numbers);
    if (!marker) return;
    el.setAttribute("data-cite-n", marker);
    if (el.hasAttribute("data-cite-text")) return;
    if (wrapPrecedingSentence(el)) {
      el.setAttribute("data-cite-text", "1");
      return;
    }
    // Nothing to wrap, so drop the title — but only when the span's text
    // really is the title. Anything else is report content (e.g. a passage
    // this pass already wrapped) and must never be discarded.
    const titles = sources
      .map((s) => s.title?.trim())
      .filter(Boolean)
      .join("; ");
    if (titles && el.textContent?.trim() === titles) {
      el.textContent = "";
    }
  });
}

/**
 * Marker-only flavour of the above, for a surface whose text must not be
 * restructured: every citation keeps its own text exactly as it stands and
 * gains a superscript marker beside it, with a legacy citation's source title
 * tucked into a hidden span rather than pulled out of the sentence.
 *
 * Used for the redline diff, where the pass above cannot run — moving a
 * sentence into a citation span would cut across the added/removed markup the
 * diff wraps that sentence in. This draws the same thing the editor's
 * decorations do (`citationMarkersExtension`), and for the same reason:
 * citations have to read as markers without the document being rewritten.
 *
 * Safe to run again over a DOM it has already marked.
 */
export function markCitationsInDom(
  root: ParentNode,
  numbers: Map<string, number>,
): void {
  root.querySelectorAll("span[data-morgan-cite]").forEach((el) => {
    if (el.hasAttribute("data-cite-marker")) return;
    if (el.nextElementSibling?.hasAttribute("data-cite-marker")) return;
    const sources = citationsFromAttr(
      el.getAttribute("data-morgan-cite") ?? "",
    );
    const marker = citationMarker(sources, numbers);
    if (!marker) return;
    const doc = el.ownerDocument;
    // A legacy citation's text is the source title, which the marker replaces.
    if (!el.hasAttribute("data-cite-text")) {
      const title = doc.createElement("span");
      title.className = CITE_TITLE_CLASS;
      title.append(...Array.from(el.childNodes));
      el.appendChild(title);
    }
    el.after(citationMarkerElement(marker, sources, doc));
  });
}

/**
 * Export flavour of the above: rewrite inline citations to a plain
 * `<sup>` marker so PDF/Word get footnote-style numbers keyed to the
 * bibliography instead of app-specific chip markup.
 */
export function renderCitationNumbersForExport(
  html: string,
  numbers: Map<string, number>,
): string {
  const doc = new DOMParser().parseFromString(
    `<div id="morgan-number-root">${html}</div>`,
    "text/html",
  );
  const root = doc.getElementById("morgan-number-root");
  if (!root) return html;
  root.querySelectorAll("span[data-morgan-cite]").forEach((el) => {
    const sources = citationsFromAttr(
      el.getAttribute("data-morgan-cite") ?? "",
    );
    const marker = citationMarker(sources, numbers);
    const sup = doc.createElement("sup");
    sup.textContent = marker;
    if (el.hasAttribute("data-cite-text")) {
      // Keep the referenced text, append the marker after it.
      el.replaceWith(
        ...Array.from(el.childNodes),
        marker ? sup : doc.createTextNode(""),
      );
    } else {
      el.replaceWith(marker ? sup : doc.createTextNode(""));
    }
  });
  return root.innerHTML;
}

/** A plain "Sources" bibliography list (for PDF/Word export). */
export function buildBibliographyHtml(sources: CitedSource[]): string {
  if (sources.length === 0) return "";
  const items = sources
    .map((s) => {
      const meta = getKindMeta(s.kind);
      const label = s.title?.trim() || `${meta.label} ${s.id.slice(0, 6)}`;
      return `<li>${label} <em>(${meta.label})</em></li>`;
    })
    .join("");
  return `<h2>Sources</h2><ol>${items}</ol>`;
}
